/**
 * Media API slice for managing media uploads
 */
import { baseApi } from "./baseApi";
import type { Media, ImageTransformOptions } from "@/types";

interface MediaListResponse {
  success: boolean;
  data: Media[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface MediaResponse {
  success: boolean;
  data: Media;
}

interface FoldersResponse {
  success: boolean;
  data: string[];
}

interface MediaFilters {
  folder?: string;
  page?: number;
  limit?: number;
}

export const mediaApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all media with optional filters
    getMedia: builder.query<MediaListResponse, MediaFilters | void>({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters?.folder) params.append("folder", filters.folder);
        if (filters?.page) params.append("page", filters.page.toString());
        if (filters?.limit) params.append("limit", filters.limit.toString());
        
        const queryString = params.toString();
        return `/media${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Media" as const, id })),
              { type: "Media", id: "LIST" },
            ]
          : [{ type: "Media", id: "LIST" }],
    }),

    // Get available folders
    getMediaFolders: builder.query<FoldersResponse, void>({
      query: () => "/media/folders",
    }),

    // Upload media file with optional transformations
    uploadMedia: builder.mutation<MediaResponse, { 
      file: File; 
      folder?: string;
      transforms?: ImageTransformOptions;
    }>({
      query: ({ file, folder, transforms }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (folder) formData.append("folder", folder);
        // Add transformation options
        if (transforms?.sharpen) formData.append("sharpen", "true");
        if (transforms?.upscale) formData.append("upscale", "true");
        if (transforms?.enhance) formData.append("enhance", "true");
        
        return {
          url: "/media",
          method: "POST",
          body: formData,
          // Don't set Content-Type header, let browser set it with boundary
          formData: true,
        };
      },
      invalidatesTags: [{ type: "Media", id: "LIST" }],
    }),

    // Import media from URL with optional transformations
    importMediaFromUrl: builder.mutation<MediaResponse, { 
      url: string; 
      folder?: string;
      transforms?: ImageTransformOptions;
    }>({
      query: ({ url, folder, transforms }) => ({
        url: "/media/import",
        method: "POST",
        body: { 
          url, 
          folder,
          sharpen: transforms?.sharpen,
          upscale: transforms?.upscale,
          enhance: transforms?.enhance,
        },
      }),
      invalidatesTags: [{ type: "Media", id: "LIST" }],
    }),

    // Delete media
    deleteMedia: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/media/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Media", id },
        { type: "Media", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetMediaQuery,
  useGetMediaFoldersQuery,
  useUploadMediaMutation,
  useImportMediaFromUrlMutation,
  useDeleteMediaMutation,
} = mediaApi;
